import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Grid, 
  Alert, 
  CircularProgress 
} from '@mui/material';
import { Build, Save, ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import axios from 'axios';

const VehicleMaintenanceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    maintenance_date: new Date(),
    cost: '',
    maintenance_type: 'regular',
    description: '',
    performed_by: '',
    notes: '',
    odometer_reading: '',
    status: 'completed'
  });
  
  useEffect(() => {
    const fetchVehicle = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/admin/vehicles/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.data.success) {
          setVehicle(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching vehicle:', error);
        setError('Failed to fetch vehicle details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchVehicle();
  }, [id]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleDateChange = (newDate) => {
    setFormData({
      ...formData,
      maintenance_date: newDate
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/admin/vehicles/${id}/maintenance`,
        {
          ...formData,
          maintenance_date: formData.maintenance_date.toISOString().split('T')[0],
          odometer_reading: formData.odometer_reading || null,
          next_maintenance_date: null,
          next_maintenance_odometer: null
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.data.success) {
        setSuccess('Maintenance record added successfully!');
        // Reset form
        setFormData({
          maintenance_date: new Date(),
          cost: '',
          maintenance_type: 'regular',
          description: '',
          performed_by: '',
          notes: '',
          odometer_reading: '',
          status: 'completed'
        });
        
        // Redirect after a short delay
        setTimeout(() => {
          navigate(`/admin/vehicles/utilization`);
        }, 2000);
      }
    } catch (error) {
      console.error('Error adding maintenance record:', error);
      setError(error.response?.data?.message || 'Failed to add maintenance record. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <div>
      <Box display="flex" alignItems="center" mb={3}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={() => navigate('/admin/vehicles/utilization')}
          sx={{ mr: 2 }}
        >
          Back to Dashboard
        </Button>
        <Typography variant="h4">
          Add Maintenance Record
        </Typography>
      </Box>
      
      {vehicle && (
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>
            {vehicle.name} ({vehicle.registration_number})
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {vehicle.model} {vehicle.model_year} • Capacity: {vehicle.capacity} • Status: {vehicle.status}
          </Typography>
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
      
      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Maintenance Date"
                    value={formData.maintenance_date}
                    onChange={handleDateChange}
                    renderInput={(params) => <TextField {...params} fullWidth required />}
                  />
                </LocalizationProvider>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="cost"
                  label="Cost"
                  type="number"
                  value={formData.cost}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: <Box component="span" mr={1}>$</Box>
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Maintenance Type</InputLabel>
                  <Select
                    name="maintenance_type"
                    value={formData.maintenance_type}
                    onChange={handleInputChange}
                    label="Maintenance Type"
                  >
                    <MenuItem value="regular">Regular Service</MenuItem>
                    <MenuItem value="major">Major Service</MenuItem>
                    <MenuItem value="repair">Repair</MenuItem>
                    <MenuItem value="inspection">Inspection</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    label="Status"
                  >
                    <MenuItem value="scheduled">Scheduled</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="performed_by"
                  label="Performed By"
                  value={formData.performed_by}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="description"
                  label="Description"
                  value={formData.description}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="odometer_reading"
                  label="Odometer Reading"
                  value={formData.odometer_reading}
                  onChange={handleInputChange}
                  fullWidth
                  type="number"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="notes"
                  label="Notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={4}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<Save />}
                    disabled={submitting}
                  >
                    {submitting ? 'Saving...' : 'Save Maintenance Record'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default VehicleMaintenanceForm;
